'use client';
import clsx from 'clsx';
import Markdown from 'markdown-to-jsx';
import { usePathname } from 'next/navigation';
import React, { FC, useEffect, useState } from 'react';

import LinksList from '1_components/LinksList/LinksList';
import PrivacyFooter from '1_components/PrivacyFooter/PrivacyFooter';
import SvgCaret from '1_components/SVGs/SvgCaret';
import { MenuCMS } from '2_sections/Header/SideMenu/SideMenu.data';

import S from './FAQSection.module.scss';

import useSideMenuScroll from 'hooks/useSideMenuScroll';
import { fonts } from 'styles/fontsRegistry';

export interface FAQSectionProps {
  data: MenuCMS;
  className?: string;
}

const FAQSection: FC<FAQSectionProps> = ({ data, className }) => {
  const [secondarySectionsHidden, setSecondarySectionsHidden] = useState(false);
  const path = usePathname();

  useSideMenuScroll();

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !window.location.search.includes('menu=disclaimers') ||
      path !== '/about-the-project'
    ) {
      return;
    }

    const scrollElement = document.querySelector('#fouc-wrapper > section');
    const disclaimer = document.getElementById('side_menu_disclaimers_article');

    if (scrollElement && disclaimer) {
      setTimeout(() => {
        scrollElement.scrollTo({
          top: disclaimer.getBoundingClientRect().top - 50,
          behavior: 'smooth',
        });
      }, 1000);
    }
  }, [path]);

  return (
    <div id="side_menu" className={clsx(S.FAQSectionWrapper, className)}>
      <div className={S.ArticleContainer}>
        <article className={S.Article}>
          <h2 className={fonts.faqHeading}>{data.heading}</h2>

          {data.sections.map(
            ({ heading, description, images }, sectionIndex) => (
              <section
                key={`faq-section-1-${sectionIndex}`}
                className={S.Section}
              >
                {heading && <h3 className={fonts.paragraph2}>{heading}</h3>}

                <Markdown className={S.Markdown}>{description}</Markdown>

                {images?.length > 0 && (
                  <div className={S.DataProviders}>
                    {images.map(({ url }, index) => (
                      <img
                        src={url}
                        key={`provider-${sectionIndex}-${index}`}
                        className={S.DataProvider}
                      />
                    ))}
                  </div>
                )}
              </section>
            )
          )}
        </article>

        <article
          id="side_menu_disclaimers_article"
          className={clsx(S.Article, S.DisclaimersArticle)}
        >
          {data.secondarySections.length > 1 && (
            <button
              style={{ rotate: secondarySectionsHidden ? '-90deg' : '90deg' }}
              onClick={() => setSecondarySectionsHidden(prev => !prev)}
              className={S.CollapseButton}
            >
              <SvgCaret />
            </button>
          )}

          <section className={S.Section}>
            <h3 className={fonts.paragraph2}>
              {data.secondarySections[0].heading}
            </h3>

            {!secondarySectionsHidden && (
              <Markdown className={S.Markdown}>
                {data.secondarySections[0].description}
              </Markdown>
            )}
          </section>

          {!secondarySectionsHidden &&
            data.secondarySections
              .slice(1)
              .map(({ heading, description }, index) => (
                <section key={`faq-section-2-${index}`} className={S.Section}>
                  <h3 className={fonts.paragraph2}>{heading}</h3>

                  <Markdown className={S.Markdown}>{description}</Markdown>
                </section>
              ))}
        </article>
      </div>

      <div className={S.LinksWrapper}>
        <LinksList links={data.links} />

        <PrivacyFooter links={data.secondaryLinks} />
      </div>
    </div>
  );
};
export default FAQSection;
